import React, { useState } from "react";
import {
  ShieldCheck, CheckCircle2, AlertTriangle, ExternalLink,
  Code, RefreshCw, Send, Globe, FileText, Check, Copy
} from "lucide-react";
import { SeoMarket } from "./types";

interface SeoTechnicalTabProps {
  market: SeoMarket;
}

export default function SeoTechnicalTab({ market }: SeoTechnicalTabProps) {
  const [isPingingGoogle, setIsPingingGoogle] = useState(false);
  const [pingSuccess, setPingSuccess] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const siteDomain = market === "NO" ? "simplyloan.no" : "simplyloan.se";
  const baseUrl = `https://${siteDomain}`;

  const handlePingGoogleIndexing = () => {
    setIsPingingGoogle(true);
    setPingSuccess(null);

    // Simulate API ping to Google Indexing API
    setTimeout(() => {
      setIsPingingGoogle(false);
      setPingSuccess("Googlebot Indexing API ping sendt! Google bot er notificeret om seneste sitemap opdatering.");
    }, 1500);
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="space-y-6">

      {/* Google Indexing API Callout (100% Free Instant Indexation) */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🚀</span>
              <h3 className="font-bold text-gray-900 text-base">
                Google Indexing API (Gratis Hurtig-Indeksering)
              </h3>
              <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800">
                200 requests / dag gratis
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Google Indexing API giver mulighed for at pinge Googlebots omgående, så nye artikler crawles inden for få timer frem for uger.
            </p>
          </div>

          <button
            onClick={handlePingGoogleIndexing}
            disabled={isPingingGoogle}
            className="px-4 py-2.5 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold text-xs rounded-xl transition flex items-center gap-2 shadow-xs shrink-0 disabled:opacity-50"
          >
            {isPingingGoogle ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Pinger Googlebot...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Ping Googlebot for nye artikler</span>
              </>
            )}
          </button>
        </div>

        {pingSuccess && (
          <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs flex items-center gap-2 font-medium">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{pingSuccess}</span>
          </div>
        )}

        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 text-xs text-gray-600 space-y-1">
          <div className="font-bold text-gray-900">Sådan virker det i baggrunden:</div>
          <p>
            Vores Smart SEO Publisher og <code>indexing_client.py</code> opdaterer automatisk <code>sitemap.xml</code> ved udgivelse via AWS CodeBuild / CloudFront og sender et signal til IndexNow samt Google Search Console.
          </p>
        </div>
      </div>

      {/* Schema.org Rich Snippets Status */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div>
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <Code className="w-4 h-4 text-indigo-600" />
            <span>Strukturerede Data & Schema.org Status (Rich Snippets)</span>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            JSON-LD Schema hjælper SimplyLoan med at få stjerner, FAQ-accordions og låneberegnere direkte i Googles søgeresultater.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          
          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">FAQPage Schema</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Implementeret på alle kategori-sider (f.eks. Samla lån, Privatlån) og dybdegående guides.
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;FAQPage&quot;
            </div>
          </div>

          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">FinancialProduct Schema</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Struktureret data for lånebeløb, nominel og effektiv rente (ÅOP) for sammenligningstabeller.
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;FinancialProduct&quot;
            </div>
          </div>

          <div className="p-4 rounded-xl border border-blue-200 bg-blue-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-blue-900">Article / Guide Schema</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                Aktiv
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Understøtter Google E-E-A-T ved at angive forfatter, faktatjekker og udgivelsesdato.
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;Article&quot;
            </div>
          </div>

        </div>
      </div>

      {/* Core Technical Files: Sitemap & Robots.txt */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Sitemap.xml */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0F3F34]" />
              <span>Sitemap.xml (Live på serveren)</span>
            </div>
            <a
              href={`${baseUrl}/sitemap.xml`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-[#0F3F34] hover:underline flex items-center gap-1"
            >
              <span>Åbn live xml</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 font-mono text-xs text-gray-700 space-y-1">
            <div className="text-gray-400">&lt;urlset xmlns=&quot;http://www.sitemaps.org/schemas/sitemap/0.9&quot;&gt;</div>
            {market === "NO" ? (
              <>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lan-penger/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lan-penger/samlelan/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lan-med-betalingsanmerkninger/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/artikler/...&lt;/loc&gt;</div>
              </>
            ) : (
              <>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/samla-lan&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lana-pengar&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lan-med-betalningsanmarkning&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/guide/...&lt;/loc&gt;</div>
              </>
            )}
            <div className="text-gray-400">&lt;/urlset&gt;</div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Indeholder alle 30+ hubs og dynamiske guides</span>
            <button
              onClick={() => copyToClipboard(`${baseUrl}/sitemap.xml`, "sitemap")}
              className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              {copiedKey === "sitemap" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedKey === "sitemap" ? "Kopieret!" : "Kopiér URL til GSC"}</span>
            </button>
          </div>
        </div>

        {/* Robots.txt */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0F3F34]" />
              <span>Robots.txt (Crawl Directives)</span>
            </div>
            <a
              href={`${baseUrl}/robots.txt`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-[#0F3F34] hover:underline flex items-center gap-1"
            >
              <span>Åbn live robots</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 font-mono text-xs text-gray-700 space-y-1">
            <div className="text-gray-500">User-agent: *</div>
            <div className="text-emerald-800 font-bold">Allow: /</div>
            <div className="text-gray-400"># Sitemap declaration</div>
            <div className="text-blue-700">Sitemap: {baseUrl}/sitemap.xml</div>
          </div>

          <div className="text-xs text-gray-500">
            Korrekt konfigureret til at tillade alle søgemaskiner og henvise direkte til sitemap.xml.
          </div>
        </div>

      </div>

      {/* Free Screaming Frog Audit Checklist */}
      <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <span>🐸</span>
            <span>Screaming Frog SEO Spider (Gratis op til 500 URLs)</span>
          </h4>
          <a
            href="https://www.screamingfrog.co.uk/seo-spider/"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-bold text-gray-700 hover:text-black flex items-center gap-1"
          >
            <span>Hent gratis version</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        <p className="text-xs text-gray-600">
          Da SimplyLoan på nuværende tidspunkt har ~35 sider, kan du køre en komplet teknisk site audit 100% gratis i Screaming Frog for at opdage eventuelle brudte links, manglende H1-tags, eller meta description advarsler.
        </p>
      </div>

    </div>
  );
}
